import { useCallback, useState } from 'react';

import type { Strategy } from '@/features/auth/login/types/login';
import { initiateOAuthRedirect } from '@/features/auth/login/utils/oauthRedirect';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { useSignIn } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface UseOAuthLoginReturn {
  handleSocialLogin: (strategy: Strategy) => Promise<void>;
  oauthLoading: boolean;
}

export const useOAuthLogin = (setError: (error: AuthError | null) => void): UseOAuthLoginReturn => {
  const { signIn } = useSignIn();
  const { t } = useTranslation();
  const [oauthLoading, setOauthLoading] = useState(false);

  const handleSocialLogin = useCallback(
    async (strategy: Strategy) => {
      setError(null);
      setOauthLoading(true);

      try {
        await initiateOAuthRedirect(signIn, strategy);
      } catch (err) {
        const mappedError = mapClerkError(err, t);
        setError(mappedError);
        toast.error(mappedError.message);
        setOauthLoading(false);
      }
    },
    [signIn, setError, t]
  );

  return { handleSocialLogin, oauthLoading };
};
